const CHROMIUM_FETCH_FAILED_PATTERN = /^failed to fetch(?:\s*\([^)]*\))?$/i;
const FIREFOX_FETCH_FAILED_PATTERN =
  /^(?:fetch\(\):\s*)?networkerror when attempting to fetch resource\.?(?:\s*\([^)]*\))?$/i;
const WEBKIT_FETCH_FAILED_PATTERN = /^load failed(?:\s*\([^)]*\))?$/i;
const ABORT_WITHOUT_REASON_PATTERN = /aborted without reason/i;

export const CONNECTION_ERROR_MESSAGE =
  'We’re having trouble connecting. Please try again in a moment.';

export function isAbortError(error: unknown): boolean {
  if (!error) return false;
  if (error instanceof DOMException) {
    return error.name === 'AbortError';
  }
  if (error instanceof Error) {
    return (
      error.name === 'AbortError' ||
      ABORT_WITHOUT_REASON_PATTERN.test(error.message)
    );
  }
  if (typeof error === 'string') {
    return ABORT_WITHOUT_REASON_PATTERN.test(error);
  }
  return false;
}

export function isFailedFetchError(error: unknown): boolean {
  if (!error) return false;

  const message =
    error instanceof Error
      ? error.message.trim()
      : typeof error === 'string'
        ? error.trim()
        : '';
  if (!message) return false;

  // Preserve the existing string/Error handling for Chromium's exact fetch
  // failure while excluding errors such as "Failed to fetch dynamically
  // imported module". Firefox and WebKit use more generic wording, so only
  // classify those messages when the browser actually supplied a TypeError.
  if (CHROMIUM_FETCH_FAILED_PATTERN.test(message)) return true;

  const isTypeError =
    error instanceof TypeError ||
    (error instanceof Error && error.name === 'TypeError');
  return Boolean(
    isTypeError &&
      (FIREFOX_FETCH_FAILED_PATTERN.test(message) ||
        WEBKIT_FETCH_FAILED_PATTERN.test(message)),
  );
}

export function getSafeNetworkErrorMessage(
  error: unknown,
  fallback = 'Network error. Please try again.',
): string {
  if (isFailedFetchError(error)) {
    return fallback;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return fallback;
}

export function toSafeError(
  error: unknown,
  fallback = 'Network error. Please try again.',
): Error {
  if (isAbortError(error)) {
    const abortError = new Error(
      error instanceof Error && error.message ? error.message : 'Request aborted.',
    );
    abortError.name = 'AbortError';
    return abortError;
  }
  return new Error(getSafeNetworkErrorMessage(error, fallback));
}
